import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import {
  TABLE_FORMATS,
  TABLE_NAME_MAX,
  TABLE_RESULT_MAX,
  TABLE_WEIGHT_MAX,
  expandInlineRolls,
  describeRoll,
  rollTable,
  secureRandom,
  slugify,
  writeTable,
  writeTables,
  type RandomTable,
  type TableEntry,
  type TableFormat,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile } from '../db/protocol.ts';
import { recordRoll } from '../dice/diceStore.ts';
import { saveBytes } from '../files.ts';
import { settings } from '../settings.ts';
import { TableImport } from './TableImport.tsx';

/**
 * A realm's random tables (docs/PARITY-STEP-2-PLAN.md section 4): the desktop's tables
 * gallery and table view (in its Game Master Toolkit), as pages of the realm. Roll one, edit
 * its results and weights, import tables from files or pasted text, and export them.
 */

const tablesRoute = getRouteApi('/_app/realm/$realmKey/tables');
const tableRoute = getRouteApi('/_app/realm/$realmKey/tables/$tableId');

const MIME: Record<TableFormat, string> = { CSV: 'text/csv', JSON: 'application/json', TEXT: 'text/plain', MARKDOWN: 'text/markdown' };
const FORMATS = Object.keys(TABLE_FORMATS) as TableFormat[];

function save(text: string, name: string, format: TableFormat) {
  saveBytes(new TextEncoder().encode(text), `${slugify(name)}.${TABLE_FORMATS[format].extension}`, MIME[format]);
}

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);

export function TablesPage() {
  const tables = tablesRoute.useLoaderData();
  const { realmKey } = tablesRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<'none' | 'new' | 'import'>('none');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  async function create(event: FormEvent) {
    event.preventDefault();
    try {
      const { id } = await realms.createTable(file, name, '');
      await router.invalidate();
      void navigate({ to: '/realm/$realmKey/tables/$tableId', params: { realmKey, tableId: id } });
    } catch (e) {
      setError(message(e, 'The table could not be made.'));
    }
  }

  async function exportAll(format: TableFormat) {
    try {
      const full = (await Promise.all(tables.map((t) => realms.getTable(file, t.id)))).filter((t): t is RandomTable => t !== null);
      save(writeTables(full, format), 'random-tables', format);
    } catch (e) {
      setError(message(e, 'The tables could not be exported.'));
    }
  }

  return (
    <article className="document tables-page">
      <p className="eyebrow">Random tables</p>
      <h1>Random tables</h1>
      <p className="lead">Roll for what turns up: encounters, weather, loot, rumours. Results can roll their own dice: write [[1d4+1]] in one.</p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setPanel('new')}>
          New table
        </button>
        <button type="button" onClick={() => setPanel('import')}>
          Import…
        </button>
        {tables.length > 0 && (
          <>
            <button type="button" className="quiet" onClick={() => void exportAll('JSON')}>
              Export all (JSON)
            </button>
            <button type="button" className="quiet" onClick={() => void exportAll('MARKDOWN')}>
              Export all (Markdown)
            </button>
          </>
        )}
      </div>
      {panel === 'new' && (
        <form className="panel" aria-label="New table" onSubmit={(e) => void create(e)}>
          <label>
            Table name
            <input value={name} maxLength={TABLE_NAME_MAX} autoFocus onChange={(e) => setName(e.currentTarget.value)} />
          </label>
          <div className="actions">
            <button type="submit" className="primary">
              Make table
            </button>
            <button type="button" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </form>
      )}
      {panel === 'import' && (
        <TableImport
          existing={null}
          onCancel={() => setPanel('none')}
          onImport={async (parsed, target) => {
            await realms.importTables(file, parsed, target);
            setPanel('none');
            await router.invalidate();
          }}
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {tables.length === 0 ? (
        <p className="meta">No tables yet. Make one, or import one.</p>
      ) : (
        <ul className="table-gallery">
          {tables.map((table) => (
            <li key={table.id}>
              <Link to="/realm/$realmKey/tables/$tableId" params={{ realmKey, tableId: table.id }}>
                {table.name}
              </Link>
              <span className="meta">
                {table.entryCount} {table.entryCount === 1 ? 'result' : 'results'}
                {table.description ? ` · ${table.description}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

interface RollShown {
  readonly text: string;
  readonly note: string;
}

export function TablePage() {
  const table = tableRoute.useLoaderData();
  const { realmKey } = tableRoute.useParams();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [rolled, setRolled] = useState<RollShown | null>(null);
  const [panel, setPanel] = useState<'none' | 'edit' | 'import' | 'delete'>('none');
  const [exportFormat, setExportFormat] = useState<TableFormat>('MARKDOWN');
  const [error, setError] = useState('');

  if (!table) {
    return (
      <article className="document">
        <h1>No such table</h1>
        <p>
          It may have been deleted. <Link to="/realm/$realmKey/tables" params={{ realmKey }}>All tables</Link>
        </p>
      </article>
    );
  }

  function roll() {
    const result = rollTable(table!.entries, secureRandom);
    if (!result) {
      setRolled({ text: '(This table has no entries yet)', note: '' });
      return;
    }
    // Results can carry their own dice: "[[1d4+1]] Drax War Hounds" rolls the number, into the Dice Roller's history.
    const expanded = expandInlineRolls(result.entry.text, secureRandom);
    expanded.rolls.forEach((inline) => recordRoll(table!.name, inline));
    const inline = expanded.rolls.length === 0 ? '' : ` · ${expanded.rolls.map(describeRoll).join(', ')}`;
    setRolled({ text: expanded.text, note: `Rolled ${result.roll} on a d${result.die}${inline}` });
  }

  async function remove() {
    try {
      await realms.deleteTable(file, table!.id);
      await router.invalidate();
      void navigate({ to: '/realm/$realmKey/tables', params: { realmKey }, replace: true });
    } catch (e) {
      setError(message(e, 'The table could not be deleted.'));
    }
  }

  return (
    <article className="document table-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey/tables" params={{ realmKey }}>
          ← All tables
        </Link>
      </p>
      <h1>{table.name}</h1>
      {table.description && <p className="lead table-description">{table.description}</p>}
      <div className="actions">
        <button type="button" onClick={() => setPanel('edit')}>
          Edit
        </button>
        <button type="button" onClick={() => setPanel('import')}>
          Import rows…
        </button>
        <label className="inline-select">
          <span className="visually-hidden">Export as</span>
          <select value={exportFormat} onChange={(e) => setExportFormat(e.currentTarget.value as TableFormat)}>
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {TABLE_FORMATS[f].name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => save(writeTable(table, exportFormat), table.name, exportFormat)}>
          Export
        </button>
        <button type="button" className="quiet" onClick={() => (settings()['general.confirmDeletes'] ? setPanel('delete') : void remove())}>
          Delete…
        </button>
      </div>

      {panel === 'delete' && (
        <div className="panel confirm" role="alertdialog" aria-label={`Delete ${table.name}`}>
          <p>Delete “{table.name}”? This can't be undone.</p>
          <div className="actions">
            <button type="button" className="danger" onClick={() => void remove()}>
              Delete table
            </button>
            <button type="button" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {panel === 'import' && (
        <TableImport
          existing={{ id: table.id, name: table.name }}
          onCancel={() => setPanel('none')}
          onImport={async (parsed, target) => {
            const { ids } = await realms.importTables(file, parsed, target);
            setPanel('none');
            await router.invalidate();
            if (target.kind === 'new' && ids[0]) void navigate({ to: '/realm/$realmKey/tables/$tableId', params: { realmKey, tableId: ids[0] } });
          }}
        />
      )}
      {panel === 'edit' && (
        <TableEditor
          table={table}
          onCancel={() => setPanel('none')}
          onSave={async (name, description, entries) => {
            await realms.updateTable(file, table.id, name, description, entries);
            setPanel('none');
            await router.invalidate();
          }}
        />
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      {panel !== 'edit' && (
        <>
          <section className="panel table-roll" aria-label="Roll">
            <div className="actions">
              <button type="button" className="primary" onClick={roll}>
                🎲 Roll
              </button>
            </div>
            {rolled && (
              <div role="status" aria-live="polite">
                <p className="table-result">{rolled.text}</p>
                {rolled.note && <p className="meta">{rolled.note}</p>}
              </div>
            )}
            <p className="meta">Tip: write [[1d4+1]] in a result to roll a number each time, e.g. “[[2d6]] Drax War Hounds”.</p>
          </section>
          <h2>Results</h2>
          {table.entries.length === 0 ? (
            <p className="meta">No results yet: choose Edit to add some.</p>
          ) : (
            <table className="table-rows">
              <thead>
                <tr>
                  <th scope="col">Roll</th>
                  <th scope="col">Result</th>
                  <th scope="col">Weight</th>
                </tr>
              </thead>
              <tbody>
                {rollsOf(table.entries).map(([range, entry], i) => (
                  <tr key={i}>
                    <td>{range}</td>
                    <td>{entry.text}</td>
                    <td>{entry.weight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </article>
  );
}

/** Each result's numbers on a die the size of the total weight, as the desktop's exports lay them out. */
function rollsOf(entries: readonly TableEntry[]): [string, TableEntry][] {
  let next = 1;
  return entries.map((entry) => {
    const range = entry.weight === 1 ? String(next) : `${next}-${next + entry.weight - 1}`;
    next += entry.weight;
    return [range, entry];
  });
}

function TableEditor({
  table,
  onSave,
  onCancel,
}: {
  table: RandomTable;
  onSave: (name: string, description: string, entries: TableEntry[]) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(table.name);
  const [description, setDescription] = useState(table.description);
  const [rows, setRows] = useState<TableEntry[]>([...table.entries]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const change = (i: number, row: Partial<TableEntry>) => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...row } : r)));
  const move = (i: number, by: number) =>
    setRows((all) => {
      const next = [...all];
      const [row] = next.splice(i, 1);
      next.splice(i + by, 0, row!);
      return next;
    });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    setError('');
    try {
      await onSave(name, description, rows);
    } catch (e) {
      setError(message(e, 'The table could not be saved.'));
      setWorking(false);
    }
  }

  return (
    <form className="panel table-editor" aria-label={`Edit ${table.name}`} onSubmit={(e) => void submit(e)}>
      <label>
        Name
        <input value={name} maxLength={TABLE_NAME_MAX} onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea rows={2} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <table className="table-rows">
        <thead>
          <tr>
            <th scope="col">Result</th>
            <th scope="col">Weight</th>
            <th scope="col">
              <span className="visually-hidden">Order</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <td>
                <input aria-label={`Result ${i + 1}`} value={row.text} maxLength={TABLE_RESULT_MAX} onChange={(e) => change(i, { text: e.currentTarget.value })} />
              </td>
              <td>
                <input
                  aria-label={`Weight of result ${i + 1}`}
                  type="number"
                  min={1}
                  max={TABLE_WEIGHT_MAX}
                  value={row.weight}
                  onChange={(e) => change(i, { weight: Math.min(TABLE_WEIGHT_MAX, Math.max(1, Math.round(Number(e.currentTarget.value)) || 1)) })}
                />
              </td>
              <td className="row-tools">
                <button type="button" className="quiet" aria-label={`Move result ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                  ↑
                </button>
                <button type="button" className="quiet" aria-label={`Move result ${i + 1} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
                  ↓
                </button>
                <button type="button" className="quiet" aria-label={`Remove result ${i + 1}`} onClick={() => setRows((all) => all.filter((_, j) => j !== i))}>
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="actions">
        <button type="button" onClick={() => setRows((all) => [...all, { text: '', weight: 1 }])}>
          + Add result
        </button>
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="primary" disabled={working}>
          Save
        </button>
        <button type="button" onClick={onCancel} disabled={working}>
          Cancel
        </button>
      </div>
    </form>
  );
}
